import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

export type CandidateDecision = 'interview' | 'probe_further' | 'reject' | 'revisit' | 'save_for_future' | 'request_more_info';

export type SortByField =
  | 'basic_expectation'
  | 'basic_expectations'
  | 'experience_level'
  | 'years_of_experience'
  | 'employment_preference'
  | 'salary_expectation'
  | 'soft_skills'
  | 'soft_skill'
  | 'personality'
  | 'work_qualities'
  | 'work_motivators'
  | 'work_style'
  | 'domain_knowledge'
  | 'score'
  | 'metric'
  | 'created_at';

export interface CandidateProgress {
  basic_expectation: boolean;
  work_qualities: boolean;
  work_motivators: boolean;
  work_style: boolean;
  soft_skill: boolean;
  personality: boolean;
  domain_knowledge: boolean;
  // Every metric flag ships with a `<metric>_minutes_left` counter beside it,
  // so this object holds more than the named keys above.
  [key: string]: boolean | number;
}

// Timing counters that ride along in `progress`. They are not metrics and must
// never be offered as one — that is what put "Basic Expectation Minutes Left"
// in the filter's metric list.
const MINUTES_LEFT_SUFFIX = '_minutes_left';

/**
 * The real metric keys in a candidate's `progress`, with the timing counters
 * dropped. Unioned across the page because a candidate who dropped out early
 * can come back with fewer keys than the rest.
 */
export const getMetricKeys = (candidates: Pick<CandidateItem, 'progress'>[]): string[] => {
  const keys = new Set<string>();

  candidates.forEach(({ progress }) => {
    Object.keys(progress ?? {})
      .filter((key) => !key.endsWith(MINUTES_LEFT_SUFFIX))
      .forEach((key) => keys.add(key));
  });

  return Array.from(keys);
};

/**
 * Turn a metric key into the label recruiters see. Shared so the filter's
 * checkboxes and the table's metric chips can never drift apart.
 */
export const formatMetricLabel = (key: string): string =>
  key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Per-metric scores, 0-100. A metric the candidate never reached comes back as
 * null, which must render as "—" rather than a misleading 0.
 *
 * The keys are the backend's score fields, and three of them are spelled
 * differently from the matching `progress` flag (`soft_skill` →
 * `soft_skills`, `work_qualities` → `work_place_qualities`, `work_motivators`
 * → `work_motivator`). METRIC_SCORE_FIELD below is what bridges the two — do
 * not index this map with a progress key directly.
 *
 * `domain_knowledge` is counted in `total_metrics_calculated` but carries no
 * score field here, so it stays scoreless until the backend adds one.
 */
export interface CandidateMetricScores {
  basic_expectation: number | null;
  soft_skills: number | null;
  work_place_qualities: number | null;
  work_style: number | null;
  work_motivator: number | null;
  personality: number | null;
}

export type MetricScoreKey = keyof CandidateMetricScores;

/** Progress/filter metric key → the field it is scored under in `metric_scores`. */
export const METRIC_SCORE_FIELD: Record<string, MetricScoreKey> = {
  basic_expectation: 'basic_expectation',
  soft_skill: 'soft_skills',
  soft_skills: 'soft_skills',
  work_qualities: 'work_place_qualities',
  work_place_qualities: 'work_place_qualities',
  work_style: 'work_style',
  work_motivators: 'work_motivator',
  work_motivator: 'work_motivator',
  personality: 'personality',
};

const toScore = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const score = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(score) ? Math.round(score) : null;
};

/**
 * A candidate's score for one metric, 0-100, or null when the payload carries
 * none — including every metric the backend does not score yet.
 */
export const getMetricScore = (candidate: CandidateItem, metric: string): number | null => {
  const field = METRIC_SCORE_FIELD[metric];
  if (!field) return null;

  return toScore(candidate.metric_scores?.[field]);
};

export interface CandidateItem {
  id: number;
  progress: CandidateProgress;
  /** Per-metric scores — read through getMetricScore, not by progress key. */
  metric_scores?: CandidateMetricScores;
  questions_answered: number;
  email: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  full_name: string;
  portfolio: string;
  phone_number: string;
  response_session_id: string;
  resume: string;
  duration_minutes: number;
  duration_minutes_left: number;
  start_time?: string;
  end_time?: string;
  /** 'completed' | 'started' | 'interrupted' | 'abandoned' */
  status: string;
  bucket: string;
  /** Decision bucket id behind `bucket`. */
  bucket_obj?: number | null;
  total_score: string;
  total_metrics_calculated: number;
  locked: boolean;
  /** 'generated' | 'not_generated' */
  report_status?: string;
  submitted_at?: string | null;
  /** 'manual_submit' | 'auto_submit_tab_exit' | … */
  submission_mode?: string | null;
  assessment: number;
  candidate: number;
}

interface CandidatesListResponse {
  count: number;
  num_pages?: number;
  next: string | null;
  previous: string | null;
  results: CandidateItem[];
}

// --- TEMPORARY client-side status normalization ---------------------------
// The backend worker that flips an expired `started` attempt to `abandoned`
// can lag badly (observed up to several days), so the dashboard keeps showing
// stale "In-progress" rows long after the candidate's assessment window has
// closed.
//
// Until that worker is dependable, we re-derive the status on the client using
// the same rule the backend intends: a `started` attempt whose `end_time` is
// already in the past is really `abandoned`. We only trust `end_time` here —
// `duration_minutes_left` has been observed to go stale too — so when
// `end_time` is missing or unparseable we leave the status untouched.
//
// To undo this once the backend cleanup is reliable: set the flag to false, or
// delete this block together with the `.map(normalizeCandidateStatus)` call.
export const NORMALIZE_EXPIRED_STARTED_AS_ABANDONED = true;

const isAssessmentWindowElapsed = (candidate: CandidateItem): boolean => {
  if (!candidate.end_time) return false;
  const endTime = new Date(candidate.end_time).getTime();
  return Number.isFinite(endTime) && endTime < Date.now();
};

const normalizeCandidateStatus = (candidate: CandidateItem): CandidateItem => {
  if (
    NORMALIZE_EXPIRED_STARTED_AS_ABANDONED &&
    candidate.status === 'started' &&
    isAssessmentWindowElapsed(candidate)
  ) {
    return { ...candidate, status: 'abandoned' };
  }
  return candidate;
};

interface UseReviewTalentsCandidatesParams {
  assessmentId?: string;
  decision?: CandidateDecision;
  search?: string;
  minScore?: number;
  maxScore?: number;
  /**
   * Metrics the score range applies to. `sort_by` names the primary one — the
   * range is scoped to that field — and `metrics` carries the full selection.
   * With none selected the range applies to the overall score.
   */
  metrics?: string[];
  sortBy?: SortByField;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

/**
 * Hook for fetching assessment candidates list with filtering and sorting
 */
export const useReviewTalentsCandidates = ({
  assessmentId,
  decision,
  search,
  minScore,
  maxScore,
  metrics,
  sortBy = 'created_at',
  sortOrder = 'desc',
  page = 1,
  pageSize = 10,
}: UseReviewTalentsCandidatesParams = {}) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);
  const metricsParam = metrics?.length ? metrics.join(',') : undefined;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviewTalentsCandidates', assessmentId, decision, search || undefined, minScore, maxScore, metricsParam, sortBy, sortOrder, page, pageSize],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const params: Record<string, string | number> = {
        assessment_id: assessmentId,
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (decision) {
        params.decision = decision;
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      if (minScore !== undefined) {
        params.min_score = minScore;
      }

      if (maxScore !== undefined) {
        params.max_score = maxScore;
      }

      if (metricsParam) {
        params.metrics = metricsParam;
      }

      const response = await apiClient.get<CandidatesListResponse>(
        '/candidate/dashboard/list/',
        { params }
      );
      return response.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch candidates';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    // Reset error flag when error is cleared
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    candidatesData: data,
    candidates: (data?.results || []).map(normalizeCandidateStatus),
    totalCount: data?.count || 0,
    totalPages: data?.num_pages ?? Math.ceil((data?.count || 0) / pageSize),
    nextPage: data?.next,
    previousPage: data?.previous,
    isLoading,
    error,
    refetch,
  };
};
